import ExcelJS from "exceljs";
import { RoomPropertyEnum } from "../../constants/NavbarList";
import { ExcelBoolean, ExcelEmptyDefault } from "../constants/defaults";
import {
  VersaExcelHeader,
  VersaExcelSheetName,
  VersaExcelSourceField,
} from "../enums/versaExcel";
import { versaExcelColumn } from "../enums/typedExcel";
import { versaExcelExportSheets } from "../products/versa";
import {
  ExcelExportColumn,
  ExcelExportWorksheetConfig,
  excelColumn,
} from "../types";
import { isBooleanColumnDefault, isNumericColumnDefault } from "./payloadRows";
import { readLinkedValues } from "./multiValueExport";
import { resolveRoomDefinitionListOrder } from "./roomDefinitionRows";

const MULTI_VALUE_FIELDS = new Set([
  "buildingCode",
  "connectingRoomCodes",
  "suiteRoomCodes",
  "roomFeatures",
  "roomLocations",
]);

/** Headers that must be filled for import (yellow in the download template). */
const MANDATORY_ROOM_DEFINITION_IMPORT_HEADERS = new Set<string>([
  VersaExcelHeader.RoomNumber,
  VersaExcelHeader.RoomTypeCode,
  VersaExcelHeader.BuildingCode,
]);

/**
 * Columns for Room Definition screen import template only.
 * Matches visible (non-commented) fields on RoomTypeDefinitions — not the full
 * Visual One export sheet (IsCotNotAllowed, License dates, Floor, etc.).
 */
export const roomDefinitionScreenImportColumns: ExcelExportColumn[] = [
  // Room Information
  versaExcelColumn(
    VersaExcelHeader.RoomNumber,
    VersaExcelSourceField.RoomNumber,
  ),
  versaExcelColumn(
    VersaExcelHeader.RoomTypeCode,
    VersaExcelSourceField.RoomTypeCode,
  ),
  versaExcelColumn(
    VersaExcelHeader.BuildingCode,
    VersaExcelSourceField.BuildingCode,
  ),
  versaExcelColumn(
    VersaExcelHeader.IsSuite,
    VersaExcelSourceField.IsSuite,
    ExcelBoolean.False,
  ),
  excelColumn("condo", "condo", false),
  versaExcelColumn(
    VersaExcelHeader.IsSmoking,
    VersaExcelSourceField.IsSmoking,
    ExcelBoolean.False,
  ),
  versaExcelColumn(VersaExcelHeader.Beds, VersaExcelSourceField.Beds, 0),
  versaExcelColumn(
    VersaExcelHeader.ConnectingRoomCodes,
    VersaExcelSourceField.ConnectingRoomCodes,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.RoomFeatures,
    VersaExcelSourceField.RoomFeatures,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.RoomLocations,
    VersaExcelSourceField.RoomLocations,
    ExcelEmptyDefault.Empty,
  ),
  // Points Information
  versaExcelColumn(
    VersaExcelHeader.HousekeepingSection,
    VersaExcelSourceField.HousekeepingSection,
  ),
  versaExcelColumn(
    VersaExcelHeader.StayOverPoints,
    VersaExcelSourceField.StayOverPoints,
    0,
  ),
  versaExcelColumn(
    VersaExcelHeader.CheckPoints,
    VersaExcelSourceField.CheckPoints,
    0,
  ),
  // Condo ? (shown when condo is checked)
  versaExcelColumn(
    VersaExcelHeader.CondoDescription,
    VersaExcelSourceField.CondoDescription,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoFirstName,
    VersaExcelSourceField.CondoFirstName,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoLastName,
    VersaExcelSourceField.CondoLastName,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoNightAvailable,
    VersaExcelSourceField.CondoNightAvailable,
    ExcelBoolean.False,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoRentedToGuest,
    VersaExcelSourceField.CondoRentedToGuest,
    ExcelBoolean.False,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoRentedToOwner,
    VersaExcelSourceField.CondoRendedToOwner,
    ExcelBoolean.False,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoRoomRevenue,
    VersaExcelSourceField.CondoRoomRevenue,
    0,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoUnitCondition,
    VersaExcelSourceField.CondoUnitCondition,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoAddress,
    VersaExcelSourceField.CondoAddress,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.CondoContactDetails,
    VersaExcelSourceField.CondoContactDetails,
    ExcelEmptyDefault.Empty,
  ),
  versaExcelColumn(
    VersaExcelHeader.IsActive,
    VersaExcelSourceField.IsActive,
    ExcelBoolean.True,
  ),
];

const cellToPrimitive = (value: ExcelJS.CellValue): unknown => {
  if (value == null) {
    return "";
  }
  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }
  if (typeof value === "object") {
    if ("result" in value && value.result != null) {
      return value.result;
    }
    if ("text" in value && value.text != null) {
      return value.text;
    }
    if ("richText" in value && Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text ?? "").join("");
    }
  }
  return value;
};

const parseBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if (["true", "yes", "1"].includes(trimmed)) {
      return true;
    }
    if (["false", "no", "0", ""].includes(trimmed)) {
      return false;
    }
  }
  return fallback;
};

const parseNumber = (value: unknown, fallback = 0): number | string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return fallback;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      return trimmed.includes(".") ? parsed : parsed;
    }
    return trimmed;
  }
  return fallback;
};

const isBlankCell = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return String(value).trim() === "";
};

const isRowEmpty = (row: Record<string, unknown>): boolean =>
  Object.values(row).every(isBlankCell);

const isRoomDefinitionDataRow = (row: Record<string, unknown>): boolean =>
  !isBlankCell(row[VersaExcelHeader.RoomNumber]) ||
  !isBlankCell(row[VersaExcelHeader.RoomTypeCode]) ||
  !isBlankCell(row[VersaExcelHeader.BuildingCode]);

const getRoomDefinitionWorksheetConfig = (): ExcelExportWorksheetConfig => {
  const sheetConfig = versaExcelExportSheets.find(
    (sheet) => sheet.payloadKey === RoomPropertyEnum.roomDefinitions,
  );
  if (!sheetConfig?.worksheets[0]) {
    throw new Error("Room Definition export configuration is missing.");
  }
  return sheetConfig.worksheets[0];
};

const getRoomDefinitionImportColumns = (): ExcelExportColumn[] =>
  roomDefinitionScreenImportColumns;

const applyScreenColumnDefault = (
  record: Record<string, unknown>,
  column: ExcelExportColumn,
  fieldKey: string,
): void => {
  if (MULTI_VALUE_FIELDS.has(fieldKey)) {
    record[fieldKey] = [];
    return;
  }
  if (
    isBooleanColumnDefault(column.defaultValue) ||
    typeof column.defaultValue === "boolean" ||
    fieldKey === "condo"
  ) {
    record[fieldKey] = parseBoolean(
      undefined,
      typeof column.defaultValue === "boolean" ? column.defaultValue : false,
    );
    return;
  }
  if (isNumericColumnDefault(column.defaultValue)) {
    record[fieldKey] = column.defaultValue;
    return;
  }
  if (
    column.defaultValue !== undefined &&
    column.defaultValue !== ExcelEmptyDefault.Empty
  ) {
    record[fieldKey] = column.defaultValue;
    return;
  }
  record[fieldKey] = "";
};

const mapExportRowToOnboardingRow = (
  exportRow: Record<string, unknown>,
  columns: ExcelExportColumn[],
  index: number,
): Record<string, unknown> => {
  // Only screen-template fields — do not inject Visual One-only defaults
  // (isCotNotAllowed, license dates, floor, etc.).
  const record: Record<string, unknown> = {};

  for (const column of columns) {
    const fieldKey = column.sourceField ?? column.header;
    const raw = exportRow[column.header];
    if (raw === "" || raw === null || raw === undefined) {
      applyScreenColumnDefault(record, column, fieldKey);
      continue;
    }

    if (MULTI_VALUE_FIELDS.has(fieldKey)) {
      record[fieldKey] = readLinkedValues(raw);
      continue;
    }

    if (
      isBooleanColumnDefault(column.defaultValue) ||
      typeof column.defaultValue === "boolean" ||
      fieldKey === "condo"
    ) {
      record[fieldKey] = parseBoolean(
        raw,
        typeof column.defaultValue === "boolean" ? column.defaultValue : false,
      );
      continue;
    }

    if (isNumericColumnDefault(column.defaultValue)) {
      record[fieldKey] = parseNumber(raw, column.defaultValue);
      continue;
    }

    record[fieldKey] = String(raw).trim();
  }

  const condoFields = [
    "condoDescription",
    "condoFirstName",
    "condoLastName",
    "condoAddress",
    "condoContactDetails",
    "condoUnitCondition",
  ];
  const hasCondoDetails = condoFields.some((field) => {
    const value = record[field];
    return value !== "" && value !== null && value !== undefined;
  });
  if (record.condo === undefined) {
    record.condo =
      hasCondoDetails ||
      parseBoolean(record.condoNightAvailable) ||
      parseBoolean(record.condoRentedToGuest) ||
      parseBoolean(record.condoRendedToOwner);
  } else {
    record.condo = parseBoolean(record.condo, false);
  }

  record.listOrder = resolveRoomDefinitionListOrder(record.listOrder, index);
  record.isActive = parseBoolean(record.isActive, true);

  return record;
};

const readWorksheetRows = (
  worksheet: ExcelJS.Worksheet,
): Record<string, unknown>[] => {
  const headerRow = worksheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const header = String(cellToPrimitive(cell.value) ?? "").trim();
    if (header) {
      headers[colNumber] = header;
    }
  });

  if (headers.filter(Boolean).length === 0) {
    return [];
  }

  const rows: Record<string, unknown>[] = [];
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }

    const exportRow: Record<string, unknown> = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const header = headers[colNumber];
      if (!header) {
        return;
      }
      exportRow[header] = cellToPrimitive(cell.value);
    });

    if (!isRowEmpty(exportRow) && isRoomDefinitionDataRow(exportRow)) {
      rows.push(exportRow);
    }
  });

  return rows;
};

export type RoomDefinitionImportResult = {
  rows: Record<string, unknown>[];
  errors: string[];
};

/** Parses a Room Definition .xlsx (screen import template) into onboarding rows. */
export const parseRoomDefinitionsExcelFile = async (
  file: File,
): Promise<RoomDefinitionImportResult> => {
  const columns = getRoomDefinitionImportColumns();
  const worksheetConfig = getRoomDefinitionWorksheetConfig();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());

  const worksheet =
    workbook.getWorksheet(worksheetConfig.worksheetName) ??
    workbook.getWorksheet(VersaExcelSheetName.RoomDefinition) ??
    workbook.worksheets[0];

  if (!worksheet) {
    return {
      rows: [],
      errors: ["The Excel file does not contain any worksheets."],
    };
  }

  const exportRows = readWorksheetRows(worksheet);
  if (exportRows.length === 0) {
    return {
      rows: [],
      errors: [
        "No data rows found. Use the Room Definition import template from this screen.",
      ],
    };
  }

  const errors: string[] = [];
  const mappedRows = exportRows.map((exportRow, index) =>
    mapExportRowToOnboardingRow(exportRow, columns, index),
  );

  mappedRows.forEach((row, index) => {
    const roomNumber = String(row.roomNumber ?? "").trim();
    const roomTypeCode = String(row.roomTypeCode ?? "").trim();
    const buildings = readLinkedValues(row.buildingCode);
    if (!roomNumber || !roomTypeCode) {
      errors.push(
        `Row ${index + 2}: roomNumber and roomTypeCode are required.`,
      );
    }
    if (buildings.length === 0) {
      errors.push(`Row ${index + 2}: buildingCode is required.`);
    }
  });

  if (errors.length > 0) {
    return { rows: [], errors };
  }

  const rows = mappedRows.map((row, index) => ({
    id: index + 1,
    ...row,
  }));

  return { rows, errors: [] };
};

/** Downloads a Room Definition template with only Room Definition screen fields. */
export const downloadRoomDefinitionImportTemplate = async (): Promise<void> => {
  const columns = getRoomDefinitionImportColumns();
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(VersaExcelSheetName.RoomDefinition);

  worksheet.columns = columns.map((column) => ({
    header: column.header,
    key: column.header,
    width: 18,
  }));

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const header = String(columns[colNumber - 1]?.header ?? "").trim();
    const isMandatory = MANDATORY_ROOM_DEFINITION_IMPORT_HEADERS.has(header);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isMandatory ? "FFFFFF00" : "FFE0E0E0" },
    };
    if (isMandatory) {
      cell.note = "Mandatory field";
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Room Definition.xlsx";
  link.click();
  URL.revokeObjectURL(url);
};
